<template>
  <v-container fluid>

    <!-- Filter Component -->
    <v-card class="mb-4" flat>
      <v-card-text class="pa-0">
        <hpo-filter :headers="activeHeaders" :viewName="viewName" @applyFilter="applyFilter"
          @quickFilter="quickFilter"></hpo-filter>
      </v-card-text>
    </v-card>

    <!-- Metrics Row -->
    <v-row class="mb-4" v-if="tabSummary">
      <v-col cols="12" sm="6" md="3">
        <v-card class="elevation-1" color="indigo lighten-5">
          <v-card-text class="pa-3 text-center">
            <div class="subtitle-2 grey--text text--darken-2">Tổng số đơn hàng</div>
            <div class="text-h6 font-weight-bold indigo--text">{{ tabSummary.OrderCount || 0 }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="elevation-1" color="red lighten-5">
          <v-card-text class="pa-3 text-center">
            <div class="subtitle-2 grey--text text--darken-2">Tổng phải thu</div>
            <div class="text-h6 font-weight-bold red--text">{{ tabSummary.TotalAmount | currency }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="elevation-1" color="green lighten-5">
          <v-card-text class="pa-3 text-center">
            <div class="subtitle-2 grey--text text--darken-2">Tổng đã thu</div>
            <div class="text-h6 font-weight-bold green--text">{{ tabSummary.TotalCollected | currency }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="elevation-1" :color="tabSummary.Debt < 0 ? 'orange lighten-5' : 'blue lighten-5'">
          <v-card-text class="pa-3 text-center">
            <div class="subtitle-2 grey--text text--darken-2">Tổng công nợ</div>
            <div class="text-h6 font-weight-bold" :class="tabSummary.Debt < 0 ? 'orange--text text--darken-3' : 'blue--text'">
              {{ tabSummary.Debt | currency }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs Panel -->
    <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow class="mb-4">
      <v-tab>
        <v-icon left>mdi-clock-outline</v-icon>
        Chờ thanh toán
      </v-tab>
      <v-tab>
        <v-icon left>mdi-check-circle-outline</v-icon>
        Đã hoàn thành
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Chờ thanh toán -->
      <v-tab-item>
        <!-- PHẦN 1: Phiếu giao hàng chờ thanh toán (to create requests) -->
        <v-card class="mb-4 elevation-1">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="orange">mdi-truck-delivery</v-icon>
            Danh sách khách hàng có phiếu giao hàng chờ thanh toán
            <v-spacer></v-spacer>
            <v-btn icon small @click="loadPendingDeliveries" :loading="loadingPending" title="Làm mới">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table :headers="pendingHeaders" :items="pendingCustomers" :loading="loadingPending"
              hide-default-footer disable-pagination class="elevation-0">
              <template v-slot:item.TotalAmount="{ item }">
                <b class="red--text">{{ item.TotalAmount | currency }}</b>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn small color="primary" outlined @click="openPendingDetail(item)">
                  <v-icon left small>mdi-file-document-multiple</v-icon>Tạo phiếu
                </v-btn>
              </template>
              <template v-slot:no-data>
                <span class="grey--text">Không có phiếu giao hàng nào chờ thanh toán</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- PHẦN 2: Danh sách phiếu thanh toán chờ duyệt -->
        <v-card class="elevation-1">
          <v-card-title class="subtitle-1 font-weight-bold pb-2">
            <v-icon left color="orange">mdi-cash-multiple</v-icon>
            Danh sách phiếu thanh toán chờ duyệt
          </v-card-title>
          <v-data-table :headers="headersPending" :items="pendingPayments" :options.sync="paginationPending" :server-items-length="pendingTotalRows"
            :footer-props="items_per_page" :must-sort="true" :loading="loading" class="elevation-0">
            <template v-slot:item.TotalAmount="{ item }">
              {{ item.TotalAmount | currency }}
            </template>
            <template v-slot:item.TotalCollected="{ item }">
              {{ item.TotalCollected | currency }}
            </template>
            <template v-slot:item.Debt="{ item }">
              <span :class="item.Debt < 0 ? 'orange--text text--darken-3 font-weight-bold' : ''">
                {{ item.Debt | currency }}
              </span>
            </template>
            <template v-slot:item.CreatedDate="{ item }">
              {{ item.CreatedDate | display_date }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small title="Xem chi tiết" @click="openPaymentDetail(item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn icon small color="primary" title="Upload chứng từ" @click="openUploadDialog(item)">
                <v-icon>mdi-upload</v-icon>
              </v-btn>
              <v-btn v-if="roleCanApprove" icon small color="success" title="Duyệt thanh toán" @click="quickApprove(item)" :disabled="!hasInvoice(item)">
                <v-icon>mdi-check-bold</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Đã hoàn thành -->
      <v-tab-item>
        <v-card class="elevation-1">
          <v-card-title class="subtitle-1 font-weight-bold pb-2">
            <v-icon left color="green">mdi-cash-check</v-icon>
            Danh sách phiếu thanh toán đã hoàn thành / hủy
          </v-card-title>
          <v-data-table :headers="headersCompleted" :items="completedPayments" :options.sync="paginationCompleted" :server-items-length="completedTotalRows"
            :footer-props="items_per_page" :must-sort="true" :loading="loading" class="elevation-0">
            <template v-slot:item.Status="{ item }">
              <v-chip :color="item.Status === 1 ? 'green' : 'red'" dark small>
                {{ item.Status === 1 ? 'Đã Thanh Toán' : 'Hủy' }}
              </v-chip>
            </template>
            <template v-slot:item.TotalAmount="{ item }">
              {{ item.TotalAmount | currency }}
            </template>
            <template v-slot:item.TotalCollected="{ item }">
              {{ item.TotalCollected | currency }}
            </template>
            <template v-slot:item.Debt="{ item }">
              <span :class="item.Debt < 0 ? 'orange--text text--darken-3 font-weight-bold' : ''">
                {{ item.Debt | currency }}
              </span>
            </template>
            <template v-slot:item.CreatedDate="{ item }">
              {{ item.CreatedDate | display_date }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon title="Xem chi tiết" @click="openPaymentDetail(item)">
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialog: Chọn phiếu giao hàng để tạo phiếu thanh toán -->
    <v-dialog v-model="dialogPending" max-width="800px">
      <v-card v-if="selectedCustomer">
        <v-card-title>
          <v-icon left>mdi-account</v-icon>
          {{ selectedCustomer.CustomerName }} — Chọn phiếu giao hàng
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogPending = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table v-model="selectedDeliveries" :headers="deliverySelectHeaders"
            :items="selectedCustomer.orders" show-select item-key="ID"
            class="elevation-1" disable-pagination hide-default-footer>
            <template v-slot:item.TotalAmount="{ item }">
              {{ item.TotalAmount | currency }}
            </template>
          </v-data-table>
          <v-row class="mt-3" v-if="selectedDeliveries.length > 0">
            <v-col cols="12" class="text-right">
              Tổng phải thanh toán:
              <b class="red--text text-h6">{{ totalSelectedAmount | currency }}</b>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogPending = false">Hủy</v-btn>
          <v-btn color="primary" @click="createPaymentFromDeliveries" :loading="saving"
            :disabled="selectedDeliveries.length === 0">
            <v-icon left>mdi-cash-plus</v-icon>Tạo phiếu thanh toán
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Chi tiết phiếu thanh toán -->
    <v-dialog v-model="dialogDetail" max-width="900px">
      <v-card v-if="detailItem">
        <v-card-title>
          Chi tiết Phiếu Thanh Toán
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetail = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6"><b>Khách hàng:</b> {{ detailItem.CustomerName }}</v-col>
            <v-col cols="12" sm="6"><b>Trạng thái:</b>
              <v-chip :color="detailItem.Status === 1 ? 'green' : (detailItem.Status === 2 ? 'red' : 'orange')" dark small>
                {{ detailItem.Status === 1 ? 'Đã Thanh Toán' : (detailItem.Status === 2 ? 'Hủy' : 'Chờ Thanh Toán') }}
              </v-chip>
            </v-col>
            <v-col cols="12" sm="6"><b>Ngày tạo:</b> {{ detailItem.CreatedDate | display_date }}</v-col>
            <v-col cols="12" sm="6"><b>Tổng phải thu:</b>
              <b class="red--text">{{ detailItem.TotalAmount | currency }}</b>
            </v-col>
            <v-col cols="12" sm="6"><b>Tổng đã thu:</b>
              <b class="green--text">{{ detailItem.TotalCollected | currency }}</b>
            </v-col>
            <v-col cols="12" sm="6"><b>Công nợ:</b>
              <b :class="detailItem.Debt < 0 ? 'orange--text text--darken-3' : 'blue--text'">{{ detailItem.Debt | currency }}</b>
            </v-col>
            <v-col cols="12">
              <v-divider class="mb-2"></v-divider>
              <div class="subtitle-2 mb-2">Hóa đơn / Chứng từ thanh toán</div>
              <v-file-input v-if="detailItem.Status === 0" v-model="filesToUpload" label="Tải lên hóa đơn" multiple
                outlined dense prepend-icon="mdi-paperclip" class="mt-2">
                <template v-slot:append-outer>
                  <v-btn small color="primary" @click="uploadFiles" :loading="uploading"
                    :disabled="!filesToUpload || filesToUpload.length === 0">Upload</v-btn>
                </template>
              </v-file-input>
              <div>
                <v-chip v-for="(file, idx) in paymentFiles" :key="idx" class="ma-1"
                  :close="detailItem.Status === 0" @click:close="removeFile(idx)"
                  :href="getMediaUrl(file.url)" target="_blank">
                  {{ file.name }}
                </v-chip>
                <span v-if="paymentFiles.length === 0" class="grey--text caption">Chưa có tệp đính kèm</span>
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider class="mb-2"></v-divider>
              <div class="subtitle-2 mb-1">Danh sách đơn hàng</div>
              <v-data-table :items="detailItem.orders || []" :headers="orderHeaders"
                hide-default-footer disable-pagination dense>
                <template v-slot:item.TotalTransportFee="{ item }">
                  {{ item.TotalTransportFee | currency }}
                </template>
                <template v-slot:item.TotalTrustFee="{ item }">
                  {{ item.TotalTrustFee | currency }}
                </template>
                <template v-slot:item.TotalServiceFee="{ item }">
                  {{ item.TotalServiceFee | currency }}
                </template>
                <template v-slot:item.TotalTaxFee="{ item }">
                  {{ item.TotalTaxFee | currency }}
                </template>
                <template v-slot:item.TotalAmount="{ item }">
                  {{ item.TotalAmount | currency }}
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialogDetail = false">Đóng</v-btn>
          <v-btn v-if="detailItem.Status === 0 && roleCanApprove" color="success" @click="confirmPayment(1)"
            :disabled="paymentFiles.length === 0">
            <v-icon left>mdi-check-circle</v-icon>Duyệt (Xác nhận TT)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Upload chứng từ nhanh -->
    <v-dialog v-model="dialogUpload" max-width="500px">
      <v-card v-if="uploadItem">
        <v-card-title>
          Tải lên chứng từ — {{ uploadItem.CustomerName }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogUpload = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-file-input v-model="filesToUpload" label="Chọn tệp chứng từ" multiple outlined dense prepend-icon="mdi-paperclip" class="mt-2">
            <template v-slot:append-outer>
              <v-btn small color="primary" @click="uploadFilesQuick" :loading="uploading" :disabled="!filesToUpload || filesToUpload.length === 0">Upload</v-btn>
            </template>
          </v-file-input>
          <div class="mt-4">
            <b>Chứng từ hiện tại:</b>
            <div class="mt-2">
              <v-chip v-for="(file, idx) in quickFiles" :key="idx" class="ma-1" close @click:close="removeFileQuick(idx)" :href="getMediaUrl(file.url)" target="_blank">
                {{ file.name }}
              </v-chip>
              <span v-if="quickFiles.length === 0" class="grey--text caption">Chưa có chứng từ nào</span>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogUpload = false">Đóng</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import ApiService from '@/services/api.service'
import configs from '@/configs'
import Filtering from "@/components/Filtering"
import { sendSuccessNotice, sendErrorNotice } from '@/commons/utils'
import _ from 'lodash'

export default {
  name: 'CnPaymentList',
  components: {
    "hpo-filter": Filtering
  },
  data() {
    return {
      activeTab: 0,

      // Pending table headers
      headersPending: [
        { text: 'Khách hàng', value: 'CustomerName', filterable: true, quickSearch: true, dataType: configs.DATA_TYPE["String"] },
        { text: 'Số đơn', value: 'OrderCount', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Tổng phải thu', value: 'TotalAmount', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Tổng đã thu', value: 'TotalCollected', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Công nợ', value: 'Debt', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Ngày tạo', value: 'CreatedDate', filterable: true, dataType: configs.DATA_TYPE["Date"] },
        { text: 'Thao tác', value: 'actions', sortable: false }
      ],

      // Completed table headers
      headersCompleted: [
        { text: 'Khách hàng', value: 'CustomerName', filterable: true, quickSearch: true, dataType: configs.DATA_TYPE["String"] },
        { text: 'Số đơn', value: 'OrderCount', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Tổng phải thu', value: 'TotalAmount', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Tổng đã thu', value: 'TotalCollected', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Công nợ', value: 'Debt', align: 'end', filterable: true, dataType: configs.DATA_TYPE["Number"] },
        { text: 'Ngày tạo', value: 'CreatedDate', filterable: true, dataType: configs.DATA_TYPE["Date"] },
        { text: 'Trạng thái', value: 'Status' },
        { text: 'Thao tác', value: 'actions', sortable: false }
      ],

      paginationPending: _.cloneDeep(configs.PAGINATION),
      paginationCompleted: _.cloneDeep(configs.PAGINATION),
      items_per_page: _.cloneDeep(configs.ITEMS_PER_PAGE),

      // PHẦN 1: Chờ thanh toán (đơn hàng status 5 chưa tạo phiếu)
      pendingCustomers: [],
      loadingPending: false,
      pendingHeaders: [
        { text: 'Khách hàng', value: 'CustomerName' },
        { text: 'Số đơn hàng', value: 'OrderCount', align: 'center' },
        { text: 'Tổng phải thu', value: 'TotalAmount', align: 'end' },
        { text: 'Thao tác', value: 'actions', sortable: false, align: 'center' }
      ],
      dialogPending: false,
      selectedCustomer: null,
      selectedDeliveries: [],
      deliverySelectHeaders: [
        { text: 'Mã đơn hàng', value: 'OrderCode' },
        { text: 'Tổng phải thu', value: 'TotalAmount', align: 'end' }
      ],
      saving: false,

      // Order headers for detail dialog
      orderHeaders: [
        { text: 'Mã ĐH', value: 'OrderCode' },
        { text: 'Phí vận chuyển', value: 'TotalTransportFee', align: 'end' },
        { text: 'Phí ủy thác', value: 'TotalTrustFee', align: 'end' },
        { text: 'Dịch vụ DHD', value: 'TotalServiceFee', align: 'end' },
        { text: 'Thuế', value: 'TotalTaxFee', align: 'end' },
        { text: 'Tổng phải thu', value: 'TotalAmount', align: 'end' }
      ],

      dialogDetail: false,
      detailItem: null,
      filesToUpload: [],
      uploading: false,
      paymentFiles: [],

      // Quick Upload Dialog
      dialogUpload: false,
      uploadItem: null,
      quickFiles: [],

      viewName: 'cn_payment_list',
      filterConditions: [],
      quickFilterConditions: []
    }
  },

  computed: {
    ...mapState({
      loading: state => state.cnPayment.loading
    }),
    roleCanApprove() {
      const roleId = this.$store.state.authentication && this.$store.state.authentication.user && this.$store.state.authentication.user.roleid
      return roleId === configs.ROLE_TYPE.Admin || roleId === configs.ROLE_TYPE.Accountant || roleId === configs.ROLE_TYPE.Manager
    },
    totalSelectedAmount() {
      return this.selectedDeliveries.reduce((sum, d) => sum + parseFloat(d.TotalAmount || 0), 0)
    },
    activeHeaders() {
      return this.activeTab === 0 ? this.headersPending : this.headersCompleted
    },
    pendingPayments() {
      return (this.$store.state.cnPayment.all.pending && this.$store.state.cnPayment.all.pending.data) || []
    },
    pendingTotalRows() {
      return (this.$store.state.cnPayment.all.pending && this.$store.state.cnPayment.all.pending.total) || 0
    },
    completedPayments() {
      return (this.$store.state.cnPayment.all.completed && this.$store.state.cnPayment.all.completed.data) || []
    },
    completedTotalRows() {
      return (this.$store.state.cnPayment.all.completed && this.$store.state.cnPayment.all.completed.total) || 0
    },
    tabSummary() {
      if (this.activeTab === 0) {
        return (this.$store.state.cnPayment.all.pending && this.$store.state.cnPayment.all.pending.summary) || { OrderCount: 0, TotalAmount: 0, TotalCollected: 0, Debt: 0 }
      } else {
        return (this.$store.state.cnPayment.all.completed && this.$store.state.cnPayment.all.completed.summary) || { OrderCount: 0, TotalAmount: 0, TotalCollected: 0, Debt: 0 }
      }
    }
  },

  watch: {
    paginationPending: {
      handler() {
        if (this.activeTab === 0) {
          this.fetchPendingPayments()
        }
      },
      deep: true
    },
    paginationCompleted: {
      handler() {
        if (this.activeTab === 1) {
          this.fetchCompletedPayments()
        }
      },
      deep: true
    },
    activeTab(val) {
      this.refreshTab(val)
    }
  },

  mounted() {
    this.$root.$emit("REQUEST_ROOT_OPTION", { invisibleCreateButton: true });
    this.$root.$on("REQUEST_REFRESH_DATA", this.refreshAll);
    this.refreshAll();
  },

  beforeDestroy() {
    this.$root.$off("REQUEST_REFRESH_DATA", this.refreshAll);
  },

  methods: {
    getMediaUrl(url) {
      if (!url) return '#'
      if (url.startsWith('http')) return url;
      if (!url.startsWith('/')) url = '/' + url;
      return window.location.origin + ':8001' + url;
    },

    applyFilter(filterConditions) {
      this.filterConditions = filterConditions
      this.filter_data()
    },

    quickFilter(filterConditions) {
      this.quickFilterConditions = filterConditions
      this.filter_data()
    },

    refreshAll() {
      if (this.roleCanApprove) {
        this.loadPendingDeliveries()
      }
      this.refreshTab(this.activeTab)
    },

    refreshTab(tabIndex) {
      if (tabIndex === 0) {
        this.fetchPendingPayments()
      } else {
        this.fetchCompletedPayments()
      }
    },

    async fetchPendingPayments() {
      const statusFilter = { column: 'Status', condition: 'equal', value: 0 }
      const mergedFilter = [...this.filterConditions, statusFilter]
      
      await this.$store.dispatch('cnPayment/getList', {
        view_name: 'pending',
        pagination: this.paginationPending,
        filter: mergedFilter,
        quickFilter: this.quickFilterConditions
      })
    },

    async fetchCompletedPayments() {
      const statusFilter = { column: 'Status', condition: 'in', value: [1, 2] }
      const mergedFilter = [...this.filterConditions, statusFilter]
      
      await this.$store.dispatch('cnPayment/getList', {
        view_name: 'completed',
        pagination: this.paginationCompleted,
        filter: mergedFilter,
        quickFilter: this.quickFilterConditions
      })
    },

    filter_data() {
      this.paginationPending.page = 1
      this.paginationCompleted.page = 1
      this.refreshTab(this.activeTab)
    },

    async loadPendingDeliveries() {
      this.loadingPending = true;
      try {
        const res = await ApiService.get('/api/cn-payments/pending-deliveries/')
        this.pendingCustomers = res.data.data || []
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi khi tải phiếu giao hàng chờ thanh toán')
      } finally {
        this.loadingPending = false
      }
    },

    openPendingDetail(customer) {
      this.selectedCustomer = customer
      this.selectedDeliveries = []
      this.dialogPending = true
    },

    async createPaymentFromDeliveries() {
      if (!this.selectedCustomer || this.selectedDeliveries.length === 0) return;
      this.saving = true;
      try {
        const orderIDs = this.selectedDeliveries.map(o => o.ID);
        const payload = {
          CustomerID: this.selectedCustomer.CustomerID,
          CustomerName: this.selectedCustomer.CustomerName,
          OrderIDs: orderIDs,
          TotalAmount: this.totalSelectedAmount,
          Status: 0
        }
        await ApiService.put('/api/cn-payments/', payload)
        sendSuccessNotice('Tạo phiếu thanh toán thành công')
        this.dialogPending = false
        this.refreshAll()
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi khi tạo phiếu')
      } finally {
        this.saving = false
      }
    },

    async openPaymentDetail(item) {
      try {
        const res = await ApiService.get(`/api/cn-payments/${item.ID}`)
        this.detailItem = res.data.data

        try {
          this.paymentFiles = JSON.parse(this.detailItem.InvoiceFiles || '[]')
        } catch (e) {
          this.paymentFiles = []
        }

        this.dialogDetail = true
      } catch (e) {
        console.error(e)
      }
    },

    async uploadFiles() {
      if (!this.filesToUpload || this.filesToUpload.length === 0) return;
      this.uploading = true;

      try {
        let formData = new FormData()
        for (let i = 0; i < this.filesToUpload.length; i++) {
          formData.append('files', this.filesToUpload[i])
        }

        const res = await ApiService.post('/api/files/upload-multiple/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        if (res.data.status) {
          const uploaded = (res.data.data && res.data.data.uploaded_files) || [];
          this.paymentFiles = [...this.paymentFiles, ...uploaded]
          this.filesToUpload = []

          // save immediately
          await ApiService.post(`/api/cn-payments/${this.detailItem.ID}/status/`, {
            Status: this.detailItem.Status,
            InvoiceFiles: JSON.stringify(this.paymentFiles)
          })
          sendSuccessNotice('Upload thành công')
        }
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi upload file')
      } finally {
        this.uploading = false;
      }
    },

    async removeFile(idx) {
      this.paymentFiles.splice(idx, 1)
      try {
        await ApiService.post(`/api/cn-payments/${this.detailItem.ID}/status/`, {
          Status: this.detailItem.Status,
          InvoiceFiles: JSON.stringify(this.paymentFiles)
        })
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi khi cập nhật chứng từ')
      }
    },

    async confirmPayment(status) {
      if (!confirm(status === 1 ? 'Xác nhận Đã Thanh Toán?' : 'Xác nhận Hủy phiếu?')) return;
      try {
        await ApiService.post(`/api/cn-payments/${this.detailItem.ID}/status/`, {
          Status: status,
          InvoiceFiles: JSON.stringify(this.paymentFiles)
        })
        sendSuccessNotice('Cập nhật thành công')
        this.dialogDetail = false
        this.refreshTab(this.activeTab)
      } catch (e) {
        console.error(e)
        sendErrorNotice('Cập nhật thất bại')
      }
    },

    // Quick Action Helpers
    hasInvoice(item) {
      try {
        const files = JSON.parse(item.InvoiceFiles || '[]')
        return files.length > 0
      } catch (e) {
        return false
      }
    },

    openUploadDialog(item) {
      this.uploadItem = item
      try {
        this.quickFiles = JSON.parse(item.InvoiceFiles || '[]')
      } catch (e) {
        this.quickFiles = []
      }
      this.filesToUpload = []
      this.dialogUpload = true
    },

    async uploadFilesQuick() {
      if (!this.filesToUpload || this.filesToUpload.length === 0) return;
      this.uploading = true;
      try {
        let formData = new FormData()
        for (let i = 0; i < this.filesToUpload.length; i++) {
          formData.append('files', this.filesToUpload[i])
        }

        const res = await ApiService.post('/api/files/upload-multiple/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        if (res.data.status) {
          const uploaded = (res.data.data && res.data.data.uploaded_files) || [];
          this.quickFiles = [...this.quickFiles, ...uploaded]
          this.filesToUpload = []

          // update in DB
          await ApiService.post(`/api/cn-payments/${this.uploadItem.ID}/status/`, {
            Status: this.uploadItem.Status,
            InvoiceFiles: JSON.stringify(this.quickFiles)
          })
          
          this.uploadItem.InvoiceFiles = JSON.stringify(this.quickFiles)
          sendSuccessNotice('Upload chứng từ thành công')
          this.dialogUpload = false
          this.refreshTab(0)
        }
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi upload file')
      } finally {
        this.uploading = false;
      }
    },

    async removeFileQuick(idx) {
      this.quickFiles.splice(idx, 1)
      try {
        await ApiService.post(`/api/cn-payments/${this.uploadItem.ID}/status/`, {
          Status: this.uploadItem.Status,
          InvoiceFiles: JSON.stringify(this.quickFiles)
        })
        this.uploadItem.InvoiceFiles = JSON.stringify(this.quickFiles)
        sendSuccessNotice('Đã xóa chứng từ')
        this.refreshTab(0)
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi khi xóa chứng từ')
      }
    },

    async quickApprove(item) {
      let files = []
      try {
        files = JSON.parse(item.InvoiceFiles || '[]')
      } catch (e) {
        files = []
      }
      if (files.length === 0) {
        sendErrorNotice('Vui lòng upload chứng từ trước khi duyệt thanh toán!')
        return
      }
      if (!confirm('Xác nhận Đã Thanh Toán cho phiếu này?')) return;
      try {
        await ApiService.post(`/api/cn-payments/${item.ID}/status/`, {
          Status: 1,
          InvoiceFiles: JSON.stringify(files)
        })
        sendSuccessNotice('Duyệt thanh toán thành công')
        this.refreshTab(0)
      } catch (e) {
        console.error(e)
        sendErrorNotice('Lỗi khi duyệt thanh toán')
      }
    }
  }
}
</script>
